// features/communities/useCommunityPosts.ts — a community's posts, a page at a time.
//
// The server pages them (`GET /posts/?feed=community&community=<slug>`, newest first,
// with every feed exclusion applied), so a community opened directly (a shared link, a
// reload) shows its posts without Home having loaded them first, and a busy community no
// longer costs one request per post. The cards render from postStore.
//
// A post this member just published is added to the FRONT of `community.postIds` by
// communitiesService.createCommunityPost. Any ids ahead of the first post the server
// returned are therefore newer than the loaded pages, and are shown on top.
import { useCallback, useMemo, useState } from 'react';
import { usePosts, useSession } from '@/hooks';
import { useAsyncRead } from '@/hooks/useAsyncRead';
import { postsService } from '@/core/services/postsService';
import type { Post } from '@/core/models/post';
import type { Community } from '@/core/models/community';
import { communityPosts } from './filterCommunities';

interface Page {
  ids: string[];
  next: number | null;
}

export interface CommunityPosts {
  posts: Post[];
  /** True until the first page has arrived. */
  loading: boolean;
  /** The first page failed; `retry` asks again. */
  error: boolean;
  retry: () => void;
  hasMore: boolean;
  loadingMore: boolean;
  /** The last "Load more" failed; the next tap tries again. */
  moreFailed: boolean;
  loadMore: () => void;
}

export function useCommunityPosts(community: Pick<Community, 'id' | 'postIds'>): CommunityPosts {
  const slug = community.id;
  const { posts } = usePosts();
  const { currentUser } = useSession();

  const loadFirst = useCallback(() => postsService.listCommunity(slug, 0), [slug]);
  const first = useAsyncRead<Page | null>(loadFirst, `${slug}:${currentUser?.id ?? ''}`, null);

  // Later pages, from "Load more". Tagged with the community they belong to, so moving
  // to another community never shows the previous one's pages.
  const [more, setMore] = useState<{ slug: string; ids: string[]; next: number | null } | null>(
    null,
  );
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreFailed, setMoreFailed] = useState(false);
  const extra = more?.slug === slug ? more : null;

  const loadedIds = useMemo(() => {
    const ids = [...(first.data?.ids ?? [])];
    const seen = new Set(ids);
    for (const id of extra?.ids ?? []) if (!seen.has(id)) ids.push(id);
    return ids;
  }, [first.data, extra]);
  const next = extra ? extra.next : (first.data?.next ?? null);

  const ordered = useMemo(() => {
    const loaded = new Set(loadedIds);
    const fresh: string[] = [];
    for (const id of community.postIds) {
      if (loaded.has(id)) break;
      fresh.push(id);
    }
    // Only once the first page has arrived: before that, every id would look "fresh".
    return first.data ? [...fresh, ...loadedIds] : [];
  }, [community.postIds, loadedIds, first.data]);
  const visible = useMemo(() => communityPosts(ordered, posts), [ordered, posts]);

  async function loadMore() {
    if (next == null || loadingMore) return;
    setLoadingMore(true);
    setMoreFailed(false);
    try {
      const page = await postsService.listCommunity(slug, next);
      setMore((current) => {
        const base = current?.slug === slug ? current.ids : [];
        return { slug, ids: [...base, ...page.ids], next: page.next };
      });
    } catch {
      // The button stays and says so, so the member can simply try again.
      setMoreFailed(true);
    } finally {
      setLoadingMore(false);
    }
  }

  return {
    posts: visible,
    loading: first.loading,
    error: first.error,
    retry: first.retry,
    hasMore: next != null,
    loadingMore,
    moreFailed,
    loadMore: () => void loadMore(),
  };
}
